import type { DropEvent, FileDropItem } from "react-aria";
import type {
  FieldValues,
  Path,
  PathValue,
  UseFormReturn,
  UseFormSetError,
} from "react-hook-form";

import { AttachmentCategoryEnum, ModelsEnum } from "@nlc/shared-types";
import { useMutation } from "@tanstack/react-query";
import { nanoid } from "nanoid";
import { useState } from "react";
import * as z from "zod";

import type { LinkAsset } from "@/features/request/create/shared/assets-section/types.ts";

import { isFileAsset } from "@/features/request/create/shared/assets-section/utils/uploadUtils.ts";
import { useEditRequestModals } from "@/features/request/edit/contexts/edit-request-modals.context";
import { useFeedbackTool } from "@/features/request/feedback-tool/feedbackTool/hooks/useFeedbackTool.tsx";
import { useIsTemplate } from "@/hooks/useIsTemplate.tsx";
import {
  default as apiClient,
  default as authenticatedApi,
} from "@/lib/api/client";
import { isValidURL } from "@/utils/isValidUrl.ts";

import type { Asset, FileAsset } from "../utils/uploadUtils";

import {
  hasDuplicateFileName,
  LINK_TYPE,
  MAX_FILE_SIZE,
  MAX_FILES,
} from "../utils/uploadUtils";

interface UploadUrlFields {
  attachmentId: number;
  fields: Record<string, string>;
  presignedUrl: string;
  url: string;
}

interface UseAssetsSectionReturn {
  assets: Asset[];
  error: null | string;
  handleFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleFileDelete: (attachmentId: number) => void;
  handleFileDrop: (e: DropEvent) => void;
  handleFileSelect: (e: FileList | null) => void;
  handleLinkAdd: (url: { link: string }) => void;
  handleLinkDelete: (linkId: string) => void;
}

function useFileUpload<TFormValues extends FieldValues>(
  methods: UseFormReturn<TFormValues>,
  field: Path<TFormValues>,
) {
  const { img } = useFeedbackTool();

  return useMutation({
    mutationFn: async ({
      newFile,
    }: {
      field: Path<TFormValues>;
      newFile: FileAsset;
    }) => {
      const uploadUrlFields = await apiClient
        .query({
          fileName: newFile.file.name,
          parentId: img.id,
          parentType: ModelsEnum.ATTACHMENT,
          size: newFile.file.size,
          type: AttachmentCategoryEnum.FEEDBACK,
        })
        .get("/attachments/upload-url")
        .json<UploadUrlFields>();

      const formData = new FormData();

      for (const [key, value] of Object.entries(uploadUrlFields.fields)) {
        formData.append(key, value);
      }
      formData.append("file", newFile.file);

      const uploadResponse = await fetch(uploadUrlFields.presignedUrl, {
        body: formData,
        method: "POST",
      });

      if (!uploadResponse.ok) {
        throw new Error(`Upload failed with status ${uploadResponse.status}`);
      }

      return uploadUrlFields;
    },
    onError: (_error, variables) => {
      methods.setValue(
        field,
        methods
          .getValues(field)
          .map((asset: Asset) =>
            asset.id === variables.newFile.id
              ? { ...asset, status: "error" as const }
              : asset,
          ),
      );
    },
    onSuccess: (uploadUrlFields, variables) => {
      methods.setValue(
        field,
        methods.getValues(field)?.map((asset: Asset) =>
          asset.id === variables.newFile.id
            ? {
                ...asset,
                attachmentId: uploadUrlFields.attachmentId,
                status: "success" as const,
              }
            : asset,
        ),
      );
    },
  });
}

/**
 * Hook to manage assets section including file uploads and link management
 * @param field - The form field to manage ('assets' or 'inspirations')
 * @param setLinkFormError - Function to set errors in the link form
 */
export function useUploadAssets<TFormValues extends FieldValues>(
  field: Path<TFormValues>,
  assets: Asset[],
  setLinkFormError: UseFormSetError<LinkFormValues>,
  formMethods: UseFormReturn<TFormValues>,
): UseAssetsSectionReturn {
  const [error, setError] = useState<null | string>(null);
  const { isTemplate } = useIsTemplate();
  const { openUnlock } = useEditRequestModals();

  const { mutate: uploadAssetFile } = useFileUpload(formMethods, field);
  const { handleLinkAdd, handleLinkDelete } = useLinkManagement(
    field,
    assets,
    formMethods,
    setError,
    setLinkFormError,
  );
  const { handleFileDelete } = useFileDelete(
    field,
    assets,
    setError,
    formMethods,
  );

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    setError(null);
    if (isTemplate) {
      openUnlock();

      return;
    }

    const files = event.target.files;

    if (!files) return;

    const uploadedFiles = [...files];

    if (uploadedFiles.length + assets.length > MAX_FILES) {
      setError(`You can only upload a maximum of ${MAX_FILES} files.`);

      return;
    }

    const newFiles: FileAsset[] = uploadedFiles.map((file) => ({
      attachmentId: null,
      file,
      id: nanoid(),
      status: "uploading" as const,
    }));

    formMethods.setValue(field, [...newFiles, ...assets] as PathValue<
      TFormValues,
      Path<TFormValues>
    >);

    // Validate and upload each file
    for (const file of newFiles) {
      if (hasDuplicateFileName(file.file.name, assets)) {
        setError(`Cannot upload duplicate file: ${file.file.name}`);
        updateFileStatus(file.id, "error");
        continue;
      }

      if (file.file.size > MAX_FILE_SIZE) {
        setError(`File ${file.file.name} exceeds the maximum size of 200MB.`);
        updateFileStatus(file.id, "error");
        continue;
      }

      uploadAssetFile({ field, newFile: file });
    }
  };

  // Helper function to update file status
  const updateFileStatus = (fileId: string, status: "error" | "success") => {
    formMethods.setValue(
      field,
      formMethods
        .getValues(field)
        .map((asset: Asset) =>
          asset.id === fileId ? { ...asset, status } : asset,
        ),
    );
  };

  // Helper function to convert FileList to a synthetic event for handleFileChange
  const processFiles = (fileList: FileList) => {
    const syntheticEvent = {
      preventDefault: () => {},
      target: {
        files: fileList,
      },
    } as React.ChangeEvent<HTMLInputElement>;

    handleFileChange(syntheticEvent);
  };

  // Handler for file drop events
  const handleFileDrop = (e: DropEvent) => {
    const fileItems = e.items.filter(
      (item) => item.kind === "file",
    ) as FileDropItem[];

    if (fileItems.length > 0) {
      const dataTransfer = new DataTransfer();

      Promise.all(fileItems.map((item) => item.getFile())).then((files) => {
        for (const file of files) {
          if (file) dataTransfer.items.add(file);
        }

        processFiles(dataTransfer.files);
      });
    }
  };

  // Handler for file selection events
  const handleFileSelect = (e: FileList | null) => {
    if (e) {
      const fileList = [...e];
      const dataTransfer = new DataTransfer();

      for (const file of fileList) {
        dataTransfer.items.add(file);
      }

      processFiles(dataTransfer.files);
    }
  };

  return {
    assets,
    error,
    handleFileChange,
    handleFileDelete,
    handleFileDrop,
    handleFileSelect,
    handleLinkAdd,
    handleLinkDelete,
  };
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const createLinkSchema = (existingAssets: (FileAsset | LinkAsset)[]) =>
  z.object({
    link: z
      .string()
      .max(1000, "Link is too long.")
      .refine((val) => !val || isValidURL(val), {
        message: "Link is invalid.",
      })
      .refine(
        (val) =>
          !val ||
          !existingAssets.some((asset) => "url" in asset && asset.url === val),
        {
          message: "This link has already been added.",
        },
      ),
  });

export type LinkFormValues = z.infer<ReturnType<typeof createLinkSchema>>;

export function useFileDelete<TFormValues extends FieldValues>(
  field: Path<TFormValues>,
  assets: Asset[],
  setError: (error: null | string) => void,
  methods: UseFormReturn<TFormValues>,
): {
  handleFileDelete: (attachmentId: number) => Promise<void>;
} {
  const handleFileDelete = async (attachmentId: number) => {
    setError(null);

    const fileAsset = assets.find(
      (asset): asset is FileAsset =>
        "attachmentId" in asset && asset.attachmentId === attachmentId,
    );

    if (!fileAsset || !fileAsset.id) {
      console.error(`File not found`);

      return;
    }

    try {
      const assetsWithUploadingStatus = assets.map((asset) =>
        isFileAsset(asset) && asset.attachmentId === attachmentId
          ? { ...asset, status: "uploading" as const }
          : asset,
      );

      methods.setValue(
        field,
        assetsWithUploadingStatus as PathValue<TFormValues, Path<TFormValues>>,
      );

      if (fileAsset.attachmentId) {
        await apiClient
          .delete(`/attachments/${encodeURIComponent(fileAsset.attachmentId)}`)
          .res();
      }

      const currentAssets = methods.getValues(field);
      const filteredAssets = currentAssets.filter(
        (asset: PathValue<TFormValues, Path<TFormValues>>) =>
          !isFileAsset(asset) || asset.attachmentId !== attachmentId,
      );

      methods.setValue(
        field,
        filteredAssets as PathValue<TFormValues, Path<TFormValues>>,
      );
    } catch (error) {
      console.error(`Error deleting file ${attachmentId}:`, error);

      const updatedFilesWithError = assets.map((asset) =>
        isFileAsset(asset) && asset.attachmentId === attachmentId
          ? { ...asset, status: "error" as const }
          : asset,
      );

      methods.setValue(
        field,
        updatedFilesWithError as PathValue<TFormValues, Path<TFormValues>>,
      );
      setError(
        `Failed to delete file ${fileAsset.file.name}. Please try again.`,
      );
    }
  };

  return { handleFileDelete };
}

export function useLinkManagement<TFormValues extends FieldValues>(
  field: Path<TFormValues>,
  assets: Asset[],
  methods: UseFormReturn<TFormValues>,
  setError: (error: null | string) => void,
  setLinkFormError: UseFormSetError<LinkFormValues>,
): {
  handleLinkAdd: (url: { link: string }) => void;
  handleLinkDelete: (linkId: string) => void;
} {
  const handleLinkAdd = async (url: { link: string }) => {
    if (assets.length >= MAX_FILES) {
      setError(`You can only upload a maximum of ${MAX_FILES} files.`);

      // Hack to set useLinkForm formState.isSubmitSuccessful to false and prevent it from resetting the form
      setLinkFormError("root", {
        type: "added-assets-limit-reached",
      });

      return;
    }

    const response = await authenticatedApi
      .url("/attachments/upload-link")
      .post({
        type: LINK_TYPE[field as keyof typeof LINK_TYPE],
        url: url.link,
      })
      .json<{ attachmentId: number }>();

    methods.setValue(field, [
      { attachmentId: response.attachmentId, id: nanoid(), url: url.link },
      ...assets,
    ] as PathValue<TFormValues, Path<TFormValues>>);
  };

  const handleLinkDelete = (linkId: string) => {
    setError(null);

    const filteredAssets = assets.filter((asset) => asset.id !== linkId);

    methods.setValue(
      field,
      filteredAssets as PathValue<TFormValues, Path<TFormValues>>,
    );
  };

  return { handleLinkAdd, handleLinkDelete };
}
